import { useRef } from 'react';
import type { FieldMetadata, FieldValue } from '../../data/client';
import { Input, Textarea, useAutoGrow } from '../../ui';
import { useFieldContext } from '../FieldContext';

/** `inbound-intake.md` D18: the floor a multiline box never drops below. */
const MULTILINE_ROWS = 3;

/**
 * Inputs are prop-less like the displays. They do NOT own commit semantics:
 * whether a change is saved on blur, on Enter or on a Save button is the
 * decision of the surface that renders them (table cell, record page, wizard).
 *
 * TWO CONTROLS since D18, chosen by the field's `multiline` setting. A one-line box for a
 * document is not a smaller version of the right control, it is the wrong one: it hides
 * everything but the current line, and Enter in it does nothing at all.
 */
export function TextInput() {
  const { field, value, onChange, readOnly } = useFieldContext();
  if (field.options?.multiline === true) {
    return (
      <MultilineTextInput field={field} value={value} onChange={onChange} readOnly={readOnly} />
    );
  }
  return (
    <Input
      value={typeof value === 'string' ? value : ''}
      onChange={(event) => onChange(event.target.value)}
      disabled={readOnly}
      aria-label={field.label}
      placeholder={field.label}
    />
  );
}

interface MultilineProps {
  readonly field: FieldMetadata;
  readonly value: FieldValue;
  readonly onChange: (next: FieldValue) => Promise<void> | void;
  readonly readOnly: boolean;
}

/**
 * A TEXTAREA THAT GROWS WITH WHAT IS IN IT, three rows at its smallest.
 *
 * The measurement moved into `ui/Textarea/useAutoGrow` when the inbound mapping editor
 * (`inbound-intake.md` D19) needed the same behaviour with a different floor: two copies of
 * one measurement is how the two drift apart. The floor is still this caller's, through
 * `rows`, and nothing about the behaviour changed.
 */
function MultilineTextInput({ field, value, onChange, readOnly }: MultilineProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const text = typeof value === 'string' ? value : '';
  useAutoGrow(ref, text);

  return (
    <Textarea
      ref={ref}
      rows={MULTILINE_ROWS}
      value={text}
      onChange={(event) => onChange(event.target.value)}
      disabled={readOnly}
      aria-label={field.label}
      placeholder={field.label}
    />
  );
}
